import React, { Fragment } from 'react'
import { safeJsonParse } from '@tools/wr-catalog-base'

const TABLE_HEADINGS = [
  'Service Type',
  'Title',
  'Description',
  'Outcome',
  'PTC Platform/ Solution',
  'Remote/ Onsite',
  'Success Points',
]

const SPLASH_COLORS = {
  thingworx: '#6CC04A',
  vuforia: '#F1B434',
  windchill: '#00ACC8',
  servigistics: '#F38800',
  creo: '#F1B434',
  all: '#236192',
}

const SERVICE_TYPES = new Set([
  'success services',
  'success paths',
  'success plans',
  'support plans',
  'rapid outcomes',
  'time-based services',
  'vrds',
])

const getPlatformColor = (platform) => SPLASH_COLORS[platform.toLowerCase()] || '#236192'

const IMAGE_HOST =
  process.env.NODE_ENV === 'development' ? 'https://ptc-v2.catalogs.dev.workrails.com' : window.location.origin

const _getRow = (item, rowLabel) => item.rows.find((v) => v.label.trim().toLowerCase() === rowLabel.toLowerCase()) || {}

const sortAlphabetic = (a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' })

const getItemsByPlatformLabel = (items, platformLabel, region) =>
  items
    .filter((item) => {
      const platformRow = _getRow(item, 'Parent Platform/Solution')
      const successPointsRow = _getRow(item, 'Success Points')
      if (successPointsRow && successPointsRow.control) {
        const pointsForRegion = successPointsRow.control.constants.find((v) => v.name === region)
        if (!pointsForRegion || pointsForRegion.value / 100 <= 0 || pointsForRegion.currency !== 'points') {
          return false
        }
      }

      if (platformRow && platformRow.control) {
        return platformRow.control.some((v) => safeJsonParse(v).label === platformLabel)
      } else {
        return false
      }
    })
    .sort((a, b) => sortAlphabetic(a.name, b.name))

const getPlatforms = (items) =>
  Array.from(
    new Set(
      items
        .map((item) => {
          const platformRow = _getRow(item, 'Parent Platform/Solution')
          if (platformRow && platformRow.control) {
            return platformRow.control.map((v) => safeJsonParse(v).label)
          } else {
            return false
          }
        })
        .filter(Boolean)
        .reduce((acc, v) => [...acc, ...v], [])
        .sort((a, b) => (a.toLowerCase() === 'all' ? 1 : b.toLowerCase() === 'all' ? -1 : sortAlphabetic(a, b))),
    ),
  )

const Triangle = () => (
  <Fragment>
    <div className="shape-gradient-triangle" />
    <div className="shape-gradient-triangle shape-gradient-triangle--rotated" />
  </Fragment>
)

export default function CatalogExportTemplate(props) {
  return (
    <html>
      <head>
        <title>PTC Catalog Export.pdf</title>
        <style>{props.css}</style>
      </head>
      <body>
        <div className="page title-page">
          <img className="logo logo--white logo--right" src={`${IMAGE_HOST}/PTC-logo_KO.png`} alt="" />
          <div className="title-backdrop">
            <span className="title">Customer Success:</span>
            <span className="subtitle">Success Point Services - {props.region}</span>
          </div>
          <div className="title-page-footer">
            <span className="text--italic">{props.version}</span>
          </div>
        </div>
        <div className="page second-page">
          <img className="logo" src={`${IMAGE_HOST}/ptc-master-color.png`} alt="" />
          <span className="position-top-right">Customer Success Services</span>
          <div className="text-top-backdrop">
            <p style={{ fontSize: '1.5em' }}>
              At PTC, we understand that software and technology solutions are critical to advancing your business
              operations by solving challenges and improving outcomes. Success Services make it easy to get the most out
              of your PTC software investment through point-in-time PTC subject matter expertise to assist you along
              that journey. Success Services provide flexible, when-you-need-it support by delivering tangible outcomes
              that quickly help you reach real, lasting business value.
            </p>
          </div>
        </div>
        <div className="page third-page">
          <img className="logo" src={`${IMAGE_HOST}/ptc-master-color.png`} alt="" />
          <div className="text-backdrop-side">
            <p className="text--active text--bold text--m-b-1">Success Points</p>
            <p className="text--m-b-1">
              Success Points are an easy-to-use, flexible way to purchase our Success Services. Using Success Points as
              your digital currency allows you to quickly and easily choose the right service, to be executed at the
              right time, with minimal paperwork. With Success Points in hand and the help of your Customer Success
              Manager, you’re set up to optimally move along your Success Path to achieve results quickly and capture
              business value.
            </p>

            <p className="text--active text--bold text--m-b-1">Success Services</p>
            <p className="text--m-b-1">
              We make using your Success Points easy with our extensive catalog of Success Services. From workshops to
              reviews, coding to configurations, Success Services help to accelerate adoption and optimize productivity.
              Success services come in different forms, tailored to what you need. 
            </p>
            <p className="text--m-b-1">Here’s what you can expect from our variety of Success Services:</p>
            <ul>
              <li>
                Flexible engagements, based on duration, where you can leverage PTC expertise when and where you need it
              </li>
              <li>
                Achieve a targeted result with PTC technology to drive adoption, optimize productivity, and ultimately
                get you to value quickly
              </li>
              <li>Provide broader capabilities and insights through workshops and more</li>
            </ul>
          </div>
        </div>
        <div className="page fourth-page">
          <img className="logo logo--white" src={`${IMAGE_HOST}/PTC-logo_KO.png`} alt="" />
          <div className="text-backdrop-side text-backdrop-side--right">
            <p className="text--m-b-1">Ultimately, our Success Services will help you:</p>
            <ul>
              <li>Drive Adoption</li>
              <li>Optimize Productivity</li>
              <li>Expedite your value realization</li>
            </ul>
            <p className="text--active text--bold text--m-b-1">Choosing Customer Success Services</p>
            <p className="text--m-b-1">
              The best way to choose which services are right for you is to talk to your Customer Success Manager about
              your objectives with PTC technology. Your Customer Success Manager can provide expertise in combining
              these services into a Success Path, a prescriptive approach to quickly getting you to your business
              milestones. When choosing your services, here are a few things to consider:
            </p>

            <ul>
              <li>Do you want help planning?</li>
              <li>Do you want to ensure you are implementing best practices?</li>
              <li>Do you have opportunities for optimization?</li>
            </ul>
            <p className="text--active text--bold text--m-b-1">PTC Service Catalog</p>
            <p className="text--m-b-1">
              Welcome to the Customer Success Services Catalog! Here you can browse all our offerings including Success
              Services, Success Paths, and VRDs.
            </p>
          </div>
        </div>
        {getPlatforms(props.items).map((platform) => (
          <Fragment key={platform}>
            <div style={{ backgroundColor: getPlatformColor(platform) }} className="page">
              <img className="logo logo--white logo--right" src={`${IMAGE_HOST}/PTC-logo_KO.png`} alt="" />
              <span className="splash-heading">{platform.toLowerCase() === 'all' ? 'All Products' : platform}</span>
              <Triangle />
            </div>
            <div className="table-page">
              <table className="items-table">
                <colgroup>
                  <col className="col-type" />
                  <col className="col-title" />
                  <col className="col-description" />
                  <col className="col-outcome" />
                  <col className="col-platform" />
                  <col className="col-remote" />
                  <col className="col-points" />
                </colgroup>
                <thead>
                  <tr>
                    {TABLE_HEADINGS.map((v, i) => (
                      <th key={i}>{v}</th>
                    ))}
                  </tr>
                </thead>
                <tfoot>
                  <tr>
                    <td colSpan="8">
                      <div />
                    </td>
                  </tr>
                </tfoot>
                <tbody>
                  {getItemsByPlatformLabel(props.items, platform, props.region)
                    .map((item, i) => {
                      const getRow = _getRow.bind(null, item)
                      const dummy = document.createElement('div')

                      const serviceType = item.categories
                        .filter((v) => SERVICE_TYPES.has(v.name.toLowerCase()))
                        .map((v) => v.name)
                        .join(', ')

                      let outcome = getRow('Outcome').control
                      dummy.innerHTML = outcome
                      outcome = dummy.innerText

                      const platform = getRow('Parent Platform/Solution')
                      const platformLabels =
                        platform && platform.control
                          ? platform.control.map((v) => safeJsonParse(v).label).filter(Boolean)
                          : []

                      const successPointsRow = getRow('Success Points')
                      let points = 0
                      if (successPointsRow && successPointsRow.control) {
                        const pointsForRegion = successPointsRow.control.constants.find((v) =>
                          v.name.includes(props.region),
                        )
                        if (pointsForRegion && 'value' in pointsForRegion && pointsForRegion.currency === 'points') {
                          points = pointsForRegion.value
                        } else {
                          return null
                        }
                      } else {
                        return null
                      }

                      let remote = getRow('Remote/Onsite').control || {}
                      remote = remote.label

                      return (
                        <tr key={i}>
                          <td>{serviceType}</td>
                          <td className="text--bold">{item.name}</td>
                          <td>
                            <span className="text--left block">{item.description}</span>
                          </td>
                          <td>
                            <span className="text--left block">{outcome}</span>
                          </td>
                          <td>{platformLabels.join(', ')}</td>
                          <td>{remote}</td>
                          <td className="text--bold">{points / 100}</td>
                        </tr>
                      )
                    })
                    .filter(Boolean)}
                </tbody>
              </table>
            </div>
          </Fragment>
        ))}
        <div className="page final-page">
          <img className="logo logo--white" src={`${IMAGE_HOST}/PTC-logo_KO.png`} alt="" />
          <span className="position-top-right">Customer Success Services</span>
          <img className="page-background" src={`${IMAGE_HOST}/Last%20Page.jpg`} alt="" />
          <div className="text-bottom-backdrop">
            <div className="final-page-left-pane">
              <span className="paragraph-heading text--bold text--active">Get Started Today</span>
              To find out more about the services available to you, contact a Customer Success Manager today! Visit{' '}
              <a href="https://ptc.com/services/success-management">PTC.com/services/success-management</a> to learn
              more.
            </div>

            <div className="final-page-right-pane">
              © 2018, PTC I nc. (PTC). All rights reserved. Information described herein is furnished for informational
              use only, is subject to change without notice, and should not betaken as a guarant ee, commitment, or
              offer by PTC. PTC, the PTC logo, and all PTC product names and logos are t rademarks or registered
              trademarks of PTC and/or its subsidiaries in the United St at es and other countries. All other product or
              company names are property of their res pect ive owners . The timing of any product release, including any
              features or functionality, is s ubject to change at PTC’s discretion.
            </div>
          </div>
          <div className="title-page-footer">
            <span className="text--italic">&nbsp;</span>
          </div>
        </div>
        <div className="doc-footer">
          <div>{props.version}</div>
        </div>
      </body>
    </html>
  )
}
